import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import UpgradeDialog from "./UpgradeDialog";
import CancelIcon from "@mui/icons-material/Cancel";
import LimitDialog from "./LimitDialog";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const SubscriptionTab = ({ user, styles }) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleCancelOpen = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelClose = () => {
    setCancelDialogOpen(false);
    if (cancelSuccess) {
      // Refresh user data if needed
      // updateUser(); // Uncomment if you have a function to refresh user data
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      // Get user ID from URL
      const userId = user.uid;
      if (!userId) {
        throw new Error("User ID not found");
      }
      const response = await fetch(
        `${process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api"}/cancel-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to cancel subscription");
      }
      setCancelSuccess(true);
      setCancelLoading(false);
    } catch (err) {
      console.error("Cancel subscription error:", err);
      setCancelError(err.message);
      setCancelLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: "600",
            fontSize: "1rem",
          }}
        >
          Din prenumeration
        </Typography>

        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Status: {user.isPremium ? "Aktiv" : "Inaktiv"}
          </Typography>

          {user.isPremium && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Plan: {user.plan === "monthly" ? "Månadsvis" : "Årsvis"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, display: "none" }}>
                ID: {user.subscriptionId}
              </Typography>

              <Button
                variant="outlined"
                color="error"
                onClick={handleCancelOpen}
                startIcon={<CancelIcon />}
                sx={{ mt: 3 }}
              >
                Avbryt prenumeration
              </Button>
            </>
          )}

          {!user.isPremium && (
            <Typography variant="body2" color="text.secondary">
              Du har ingen aktiv prenumeration.
            </Typography>
          )}
        </Box>

        {/* Cancel Subscription Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={handleCancelClose}
          aria-labelledby="cancel-dialog-title"
        >
          <DialogTitle id="cancel-dialog-title" sx={{ fontWeight: "700" }}>
            {cancelSuccess
              ? "Prenumerationen avslutad"
              : "Avbryt prenumeration"}
          </DialogTitle>
          <DialogContent>
            {cancelLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            ) : cancelSuccess ? (
              <DialogContentText>
                Din prenumeration har avslutats. Du kommer att förlora tillgång
                till premiumfunktioner.
              </DialogContentText>
            ) : (
              <DialogContentText>
                Är du säker på att du vill avbryta din prenumeration? Du kommer
                att förlora tillgång till alla premiumfunktioner.
              </DialogContentText>
            )}
            {cancelError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {cancelError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            {cancelSuccess ? (
              <Button
                variant="purp"
                onClick={() => (window.location.href = "/app")}
                autoFocus
              >
                Stäng
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleCancelClose}
                  disabled={cancelLoading}
                  variant="print"
                >
                  Avbryt
                </Button>
                <Button
                  onClick={handleCancelSubscription}
                  variant="purp"
                  disabled={cancelLoading}
                  autoFocus
                >
                  Bekräfta
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
      <LimitDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        userId={user.uid}
        user={user}
        styles={styles}
        titleText="Uppgradera ditt konto"
      />
    </>
  );
};

export default SubscriptionTab;
